"use client";

import type { WorkspaceMember } from "../../types/workspaceInformation";

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import {
  AlertTriangle,
  Ban,
  BookOpen,
  FileText,
  MessageSquare,
  Settings,
  StickyNote,
  Users,
} from "lucide-react";

import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";
import { BlockStudentDialog } from "../../components/settings/BlockStudentDialog";
import { blockStudent, unblockStudent } from "../../api/workspaceSettings";

import { TUTOR_WORKSPACE_PATHS } from "@/routes/paths";
import { CoachMarker } from "@/components/CoachMarker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/utils/customToast";
import useUserStore from "@/features/auth/store/useUserStore";

export const TutorsWorkspace = () => {
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const { data: workspaceInfo, members, setMembers } = useWorkspaceInformationStore();
  const { user } = useUserStore();
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
    workspaceUserId: string;
  } | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const { workspaceId } = useParams();

  if (!workspaceInfo || !user) {
    return null;
  }

  const isWorkspaceInactive = workspaceInfo.has_user_left_workspace;
  const isWorkspaceBlocked = workspaceInfo.is_workspace_blocked_for_user;
  const students = members ? members.filter((member) => member.type === "student") : [];
  const activeStudents = students.filter((s) => !s.left_at && !s.blocked_at);
  const blockedStudents = students.filter((s) => s.blocked_at);
  const leftStudents = students.filter((s) => s.left_at);

  // Check if tutor can manage students (must be active and not blocked/left)
  const canManageStudents =
    user.role === "tutor" &&
    workspaceInfo.user_workspace_role === "tutor" &&
    !isWorkspaceInactive &&
    !isWorkspaceBlocked;

  // Get title text color based on user state
  const getTitleColor = () => {
    if (isWorkspaceBlocked) {
      return "text-red-300";
    }
    if (isWorkspaceInactive) {
      return "text-amber-500";
    }

    return "text-accent-foreground";
  };

  // Navigation cards data based on actual available pages
  const navigationCards = [
    {
      title: t("workspaces:lessons", "Lessons"),
      description: t("workspaces:lessonsManagement", "Manage your lessons"),
      icon: <BookOpen className="h-6 w-6 text-green-400" />,
      path: TUTOR_WORKSPACE_PATHS(workspaceId || "").LESSONS,
      bgGradient: "from-green-500/10 to-emerald-600/10",
      iconBg: "bg-green-500/20",
      disabled: false,
    },
    {
      title: t("workspaces:conversation", "Conversation"),
      description: t("workspaces:conversationDescription", "Chat with your students"),
      icon: <MessageSquare className="h-6 w-6 text-blue-400" />,
      path: `${TUTOR_WORKSPACE_PATHS(workspaceId || "").CHAT}/${workspaceInfo.main_conversation_id}`,
      bgGradient: "from-blue-500/10 to-indigo-600/10",
      iconBg: "bg-blue-500/20",
      disabled: false,
    },
    {
      title: t("common:materials", "Materials"),
      description: t("workspaces:materialsManagement", "Manage materials"),
      icon: <FileText className="h-6 w-6 text-purple-400" />,
      path: TUTOR_WORKSPACE_PATHS(workspaceId || "").MATERIALS,
      bgGradient: "from-purple-500/10 to-violet-600/10",
      iconBg: "bg-purple-500/20",
      disabled: true,
      soon: true,
    },
    {
      title: t("workspaces:homeworks", "Homeworks"),
      description: t("workspaces:homeworksManagement", "Manage assignments"),
      icon: (
        <Icon
          className="text-orange-400"
          height={"1.5rem"}
          icon="mdi:clipboard-text"
          width={"1.5rem"}
        />
      ),
      path: TUTOR_WORKSPACE_PATHS(workspaceId || "").HOMEWORKS,
      bgGradient: "from-orange-500/10 to-red-600/10",
      iconBg: "bg-orange-500/20",
      disabled: true,
      soon: true,
    },
    {
      title: t("workspaces:notes", "Notes"),
      description: t("workspaces:notesManagement", "Workspace notes"),
      icon: <StickyNote className="h-6 w-6 text-pink-400" />,
      path: TUTOR_WORKSPACE_PATHS(workspaceId || "").NOTES,
      bgGradient: "from-pink-500/10 to-rose-600/10",
      iconBg: "bg-pink-500/20",
      disabled: true,
      soon: true,
    },
    {
      title: t("common:settings", "Settings"),
      description: t("workspaces:settingsManagement", "Workspace settings"),
      icon: <Settings className="h-6 w-6 text-gray-400" />,
      path: TUTOR_WORKSPACE_PATHS(workspaceId || "").SETTINGS,
      bgGradient: "from-gray-500/10 to-slate-600/10",
      iconBg: "bg-gray-500/20",
      disabled: false,
    },
  ];

  const handleBlockStudent = (member: WorkspaceMember) => {
    setSelectedStudent({
      id: member.id,
      name: member.full_name,
      workspaceUserId: member.workspace_user_id,
    });
    setBlockDialogOpen(true);
  };

  const handleUnblockStudent = async (member: WorkspaceMember) => {
    if (!workspaceId || !member.workspace_user_id) return;

    setProcessingUserId(member.id);
    try {
      await unblockStudent(workspaceId, member.workspace_user_id, null);

      // Update local state
      if (setMembers && members) {
        setMembers(
          members.map((m: WorkspaceMember) =>
            m.id === member.id
              ? {
                  ...m,
                  blocked_at: null,
                  blocked_reason: null,
                  blocked_by: null,
                }
              : m,
          ),
        );
      }

      toast.success(
        t("workspaces:settings.members.studentUnblocked", "Student unblocked successfully"),
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        t("workspaces:settings.members.unblockFailed", "Failed to unblock student");

      toast.error(errorMessage);
    } finally {
      setProcessingUserId(null);
    }
  };

  const onBlockConfirm = async (reason?: string) => {
    if (!workspaceId || !selectedStudent?.workspaceUserId) return;

    try {
      const data = reason ? { reason } : null;

      await blockStudent(workspaceId, selectedStudent.workspaceUserId, data);

      // Update local state
      if (setMembers && members) {
        setMembers(
          members.map((m: WorkspaceMember) =>
            m.id === selectedStudent.id
              ? {
                  ...m,
                  blocked_at: new Date().toISOString(),
                  blocked_reason: reason || null,
                  blocked_by: user.id,
                }
              : m,
          ),
        );
      }
    } catch (error: any) {
      throw error; // Re-throw to let BlockStudentDialog handle it
    }
  };

  return (
    <ScrollArea className="h-[calc(100dvh_-_4rem)]">
      <div className="mx-auto max-w-6xl p-6">
        {/* Main Content Grid */}
        <div className="space-y-6">
          {/* Header Section */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <CoachMarker
              description={t("help:coachMarkers.tutorWorkspace.header.description" as any)}
              id="workspace-header"
              placement="bottom"
              title={t("help:coachMarkers.tutorWorkspace.header.title" as any)}
            >
              <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-none p-8 shadow-2xl">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-purple-400 to-pink-600" />
                  <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
                </div>

                <div className="relative">
                  {/* Workspace Header Info */}
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Workspace Identity */}
                    <div className="lg:col-span-2">
                      <div className="mb-4 flex items-center gap-6">
                        <motion.div
                          animate={{ scale: 1 }}
                          className="relative"
                          initial={{ scale: 0.8 }}
                          transition={{ duration: 0.3, delay: 0.2 }}
                        >
                          {/* Image backdrop only behind avatar */}
                          <div className="absolute inset-0 rounded-full opacity-30">
                            <img
                              alt={workspaceInfo.name}
                              className="h-full w-full object-cover blur-2xl"
                              src={workspaceInfo.logo || "/placeholder.svg"}
                            />
                          </div>
                          <Avatar className="relative h-20 w-20 shadow-xl">
                            <AvatarImage
                              alt={workspaceInfo.name}
                              src={workspaceInfo.logo || "/placeholder.svg"}
                            />
                            <AvatarFallback className="bg-muted/20 text-2xl font-bold">
                              {workspaceInfo.name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </motion.div>
                        <div className="flex-1">
                          <h1 className={`text-3xl font-bold md:text-4xl ${getTitleColor()}`}>
                            {workspaceInfo.name}
                          </h1>

                          {/* Alert Section under title */}
                          {(isWorkspaceInactive || isWorkspaceBlocked) && (
                            <div className="mt-3">
                              {isWorkspaceInactive && (
                                <div className="flex items-center gap-2 text-amber-500">
                                  <AlertTriangle className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    {t("workspaces:workspaceInactive.title", "Workspace Inactive")}
                                  </span>
                                </div>
                              )}
                              {isWorkspaceBlocked && (
                                <div className="flex items-center gap-2 text-red-300">
                                  <Ban className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    {t("workspaces:workspaceBlocked.title", "Access Blocked")}
                                  </span>
                                  {workspaceInfo.blocked_reason && (
                                    <span className="text-xs">
                                      - {workspaceInfo.blocked_reason}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-muted-foreground text-sm">
                              {t(
                                `common:${workspaceInfo.workspace_type}`,
                                workspaceInfo.workspace_type,
                              )}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground text-sm">
                              {t(
                                `common:roles.${workspaceInfo.user_workspace_role}`,
                                workspaceInfo.user_workspace_role,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workspace Stats */}
                    <div className="bg-muted/20 rounded-xl p-4 backdrop-blur-sm">
                      <h3 className="text-accent-foreground mb-3 text-sm font-semibold tracking-wide uppercase">
                        {t("workspaces:workspaceInfo", "Workspace Info")}
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground text-xs">
                            {t("workspaces:activeStudents", "Active Students")}
                          </span>
                          <span className="text-accent-foreground text-sm font-medium">
                            {activeStudents.length}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground text-xs">
                            {t("workspaces:totalMembers", "Total Members")}
                          </span>
                          <span className="text-accent-foreground text-sm font-medium">
                            {members?.length || 0}
                          </span>
                        </div>
                        {workspaceInfo.subjects && workspaceInfo.subjects.length > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-xs">
                              {t("workspaces:subjects", "Subjects")}
                            </span>
                            <span className="text-accent-foreground text-sm font-medium">
                              {workspaceInfo.subjects.length}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </CoachMarker>
          </motion.div>

          {/* Navigation Cards Grid */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <CoachMarker
              description={t("help:coachMarkers.tutorWorkspace.navigation.description" as any)}
              id="workspace-navigation-cards"
              placement="bottom"
              title={t("help:coachMarkers.tutorWorkspace.navigation.title" as any)}
            >
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {navigationCards.map((card) => (
                  <Card
                    key={card.title}
                    className={`bg-muted/20 text-accent-foreground group relative overflow-hidden rounded-2xl border-none p-6 shadow-lg transition-all duration-300 ${
                      card.disabled
                        ? "cursor-not-allowed opacity-60"
                        : "hover:scale-[1.02] hover:shadow-xl"
                    }`}
                  >
                    {card.disabled ? (
                      <div className="block h-full">
                        {/* Background Gradient */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                        />

                        <div className="relative">
                          <div className="mb-4 flex items-center justify-between">
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.iconBg}`}
                            >
                              {card.icon}
                            </div>
                            {card.soon && (
                              <Badge className="text-xs" variant="secondary">
                                {t("common:soon", "Soon")}
                              </Badge>
                            )}
                          </div>

                          <h3 className="mb-2 text-lg font-bold">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">{card.description}</p>
                        </div>
                      </div>
                    ) : (
                      <Link className="block h-full" to={card.path}>
                        {/* Background Gradient */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                        />

                        <div className="relative">
                          <div className="mb-4 flex items-center justify-between">
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.iconBg}`}
                            >
                              {card.icon}
                            </div>
                            <Icon
                              className="text-muted-foreground h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                              icon="mdi:arrow-right"
                            />
                          </div>

                          <h3 className="mb-2 text-lg font-bold">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">{card.description}</p>
                        </div>
                      </Link>
                    )}
                  </Card>
                ))}
              </div>
            </CoachMarker>
          </motion.div>

          {/* Students Section */}
          {students.length > 0 && !isWorkspaceInactive && !isWorkspaceBlocked && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card className="bg-foreground/50 rounded-2xl border-none p-6 shadow-lg">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-accent-foreground flex items-center gap-3 text-lg font-semibold">
                    <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl">
                      <Users className="text-accent-foreground h-5 w-5" />
                    </div>
                    {t("workspaces:students", "Students")}
                  </h3>
                </div>

                <div className="space-y-6">
                  {/* Active Students */}
                  {activeStudents.length > 0 && (
                    <div>
                      <h4 className="text-accent-foreground mb-3 text-sm font-semibold">
                        {t("workspaces:settings.members.activeMembers", "Active Students")} (
                        {activeStudents.length})
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {activeStudents.slice(0, 6).map((student) => {
                          const canManage = canManageStudents && student.id !== user.id;
                          const isProcessing = processingUserId === student.id;

                          return (
                            <motion.div
                              key={student.id}
                              animate={{ opacity: 1, scale: 1 }}
                              className="group bg-muted/50 hover:bg-muted/70 relative overflow-hidden rounded-lg p-3 transition-all duration-300"
                              initial={{ opacity: 0, scale: 0.95 }}
                              transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                              {/* Background blur effect */}
                              <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-25">
                                <img
                                  alt={student.full_name}
                                  className="h-full w-full object-cover blur-2xl"
                                  src={student.avatar_path || "/placeholder.svg"}
                                />
                              </div>

                              <div className="relative flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage
                                    alt={student.full_name}
                                    src={student.avatar_path || "/placeholder.svg"}
                                  />
                                  <AvatarFallback>
                                    {student.full_name
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>

                                <div className="min-w-0 flex-1">
                                  <h4 className="text-accent-foreground truncate text-sm font-medium">
                                    {student.full_name}
                                  </h4>
                                  <p className="text-muted-foreground text-xs">
                                    {t("common:roles.student", "Student")}
                                  </p>
                                </div>

                                {canManage && (
                                  <Button
                                    className="h-6 gap-1 px-2 text-xs opacity-0 transition-opacity group-hover:opacity-100"
                                    disabled={isProcessing}
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleBlockStudent(student)}
                                  >
                                    {isProcessing ? (
                                      <Icon className="animate-spin" icon="lucide:loader-2" />
                                    ) : (
                                      <Icon icon="mdi:block-helper" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Blocked Students */}
                  {blockedStudents.length > 0 && (
                    <div>
                      <h4 className="text-accent-foreground mb-3 text-sm font-semibold">
                        {t("workspaces:settings.members.blockedMembers", "Blocked Students")} (
                        {blockedStudents.length})
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {blockedStudents.slice(0, 6).map((student) => {
                          const canManage = canManageStudents && student.id !== user.id;
                          const isProcessing = processingUserId === student.id;

                          return (
                            <motion.div
                              key={student.id}
                              animate={{ opacity: 1, scale: 1 }}
                              className="group bg-muted/50 hover:bg-muted/70 relative overflow-hidden rounded-lg p-3 transition-all duration-300"
                              initial={{ opacity: 0, scale: 0.95 }}
                              transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                              {/* Background blur effect */}
                              <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-25">
                                <img
                                  alt={student.full_name}
                                  className="h-full w-full object-cover blur-2xl"
                                  src={student.avatar_path || "/placeholder.svg"}
                                />
                              </div>

                              <div className="relative flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage
                                    alt={student.full_name}
                                    src={student.avatar_path || "/placeholder.svg"}
                                  />
                                  <AvatarFallback>
                                    {student.full_name
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-accent-foreground truncate text-sm font-medium">
                                      {student.full_name}
                                    </h4>
                                    <Badge className="text-xs" variant="destructive">
                                      {t("workspaces:settings.members.blocked", "Blocked")}
                                    </Badge>
                                  </div>
                                  <p className="text-muted-foreground text-xs">
                                    {t("common:roles.student", "Student")}
                                  </p>
                                  {student.blocked_reason && (
                                    <p className="text-muted-foreground mt-1 truncate text-xs">
                                      {student.blocked_reason}
                                    </p>
                                  )}
                                </div>

                                {canManage && (
                                  <Button
                                    className="h-6 gap-1 px-2 text-xs opacity-0 transition-opacity group-hover:opacity-100"
                                    disabled={isProcessing}
                                    size="sm"
                                    variant="foreground"
                                    onClick={() => handleUnblockStudent(student)}
                                  >
                                    {isProcessing ? (
                                      <Icon className="animate-spin" icon="lucide:loader-2" />
                                    ) : (
                                      <Icon icon="mdi:shield-check" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Left Students */}
                  {leftStudents.length > 0 && (
                    <div>
                      <h4 className="text-accent-foreground mb-3 text-sm font-semibold">
                        {t("workspaces:settings.members.leftMembers", "Left Students")} (
                        {leftStudents.length})
                      </h4>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {leftStudents.slice(0, 6).map((student) => (
                          <motion.div
                            key={student.id}
                            animate={{ opacity: 1, scale: 1 }}
                            className="group bg-muted/50 hover:bg-muted/70 relative overflow-hidden rounded-lg p-3 transition-all duration-300"
                            initial={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            {/* Background blur effect */}
                            <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-25">
                              <img
                                alt={student.full_name}
                                className="h-full w-full object-cover blur-2xl"
                                src={student.avatar_path || "/placeholder.svg"}
                              />
                            </div>

                            <div className="relative flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage
                                  alt={student.full_name}
                                  src={student.avatar_path || "/placeholder.svg"}
                                />
                                <AvatarFallback>
                                  {student.full_name
                                    .split(" ")
                                    .map((n: string) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-accent-foreground truncate text-sm font-medium">
                                    {student.full_name}
                                  </h4>
                                  <Badge className="text-xs" variant="outline">
                                    {t("workspaces:settings.members.left", "Left")}
                                  </Badge>
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  {t("common:roles.student", "Student")}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* View All Button */}
                {students.length > 6 && (
                  <div className="mt-6 text-center">
                    <Button asChild className="h-8 gap-2 px-4 text-xs" size="sm" variant="outline">
                      <Link to={TUTOR_WORKSPACE_PATHS(workspaceId || "").STUDENTS}>
                        {t("workspaces:viewAllStudents", "View All Students")}
                      </Link>
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      <BlockStudentDialog
        isOpen={blockDialogOpen}
        studentName={selectedStudent?.name || ""}
        onClose={() => {
          setBlockDialogOpen(false);
          setSelectedStudent(null);
        }}
        onConfirm={onBlockConfirm}
      />
    </ScrollArea>
  );
};
